import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, Filter, ChevronDown, Download, RefreshCw, Send, Building, TrendingUp } from 'lucide-react';
import { mdToSafeHtml } from '../../lib/markdown';

const CONF_COLORS = { high: '#16a34a', medium: '#f59e0b', low: '#ef4444' };

const DEFAULT_SUGGESTIONS = [
  'Does this carrier allow knob and tube wiring in GA?',
  'Which carriers accept 18-year-old roofs in SC?',
  'What about rental properties with prior claims?',
];

const EmptyState = ({ popularQs, onPick }) => (
  <div className="py-10 max-w-xl mx-auto">
    <div className="text-center mb-6">
      <Search size={40} className="mx-auto text-gray-200 mb-4" />
      <h3 className="text-lg font-bold text-gray-800 mb-1">Ask about underwriting appetite</h3>
      <p className="text-sm text-gray-400 max-w-md mx-auto">Search uploaded carrier guidelines using natural language. Get cited answers grounded in your documents.</p>
    </div>
    {popularQs.length > 0 ? (
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid="popular-questions">
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center gap-1.5">
          <TrendingUp size={11} className="text-[#101C47]" />
          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Most-asked questions</span>
        </div>
        <div className="divide-y divide-gray-50">
          {popularQs.map((pq, i) => (
            <button key={pq.question} onClick={() => onPick(pq.question)}
              className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-blue-50/50 transition-colors group"
              data-testid={`popular-question-${i}`}>
              <span className="text-[10px] font-bold text-gray-300 w-4 flex-shrink-0 group-hover:text-[#101C47]">{i + 1}</span>
              <span className="text-xs text-gray-600 flex-1 truncate group-hover:text-gray-900">{pq.question}</span>
              {pq.count > 1 && <span className="text-[9px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full flex-shrink-0">×{pq.count}</span>}
            </button>
          ))}
        </div>
      </div>
    ) : (
      <div className="flex flex-wrap gap-2 justify-center">
        {DEFAULT_SUGGESTIONS.map(q => (
          <button key={q} onClick={() => onPick(q)} className="text-[10px] text-gray-500 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-full transition-colors">{q}</button>
        ))}
      </div>
    )}
  </div>
);

const ChatMessage = ({ msg }) => (
  <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
    <div className={`max-w-[85%] ${msg.role === 'user' ? 'bg-[#101C47] text-white rounded-2xl rounded-br-md px-4 py-2.5' : 'bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm'}`}>
      {msg.role === 'user' ? (
        <p className="text-sm">{msg.content}</p>
      ) : (
        <div>
          {msg.confidence && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: CONF_COLORS[msg.confidence] }}>
                {msg.confidence.toUpperCase()} CONFIDENCE
              </span>
            </div>
          )}
          <div className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: mdToSafeHtml(msg.content) }} />
          {msg.citations?.length > 0 && (
            <div className="mt-3 pt-2 border-t border-gray-100 space-y-1.5">
              <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">Sources</p>
              {msg.citations.map((c, ci) => (
                <div key={c.doc_id || ci} className="bg-gray-50 rounded-lg p-2 border border-gray-100">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Building size={10} className="text-gray-400" />
                    <span className="text-[10px] font-bold text-gray-700">{c.carrier}</span>
                    {c.state && <span className="text-[10px] text-gray-500">/ {c.state}</span>}
                    {c.lob && <span className="text-[10px] text-gray-500">/ {c.lob}</span>}
                    {c.effective_date && <span className="text-[9px] text-gray-400 ml-1">Eff: {c.effective_date}</span>}
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1 line-clamp-2">{c.excerpt}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  </div>
);

export const ChatTab = ({
  filters, popularQs, messages, question, setQuestion, chatLoading, sessionId,
  chatCarrier, setChatCarrier, chatState, setChatState, chatLob, setChatLob,
  showFilters, setShowFilters, handleChat, newSession, exportChatPDF, chatEndRef, chatInputRef,
}) => {
  const pickQuestion = (q) => { setQuestion(q); chatInputRef.current?.focus(); };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Filters bar */}
      <div className="px-6 py-2 border-b border-gray-100 bg-gray-50/50 flex items-center gap-2 flex-wrap">
        <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
          <Filter size={12} /> Filters <ChevronDown size={10} className={showFilters ? 'rotate-180' : ''} />
        </button>
        {chatCarrier && <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{chatCarrier} <button onClick={() => setChatCarrier('')} className="ml-1">&times;</button></span>}
        {chatState && <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">{chatState} <button onClick={() => setChatState('')} className="ml-1">&times;</button></span>}
        {chatLob && <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">{chatLob} <button onClick={() => setChatLob('')} className="ml-1">&times;</button></span>}
        <div className="flex-1" />
        {sessionId && (
          <button onClick={exportChatPDF} className="text-[10px] text-gray-400 hover:text-blue-600 flex items-center gap-1" data-testid="export-chat-pdf">
            <Download size={10} /> Export PDF
          </button>
        )}
        <button onClick={newSession} className="text-[10px] text-gray-400 hover:text-blue-600 flex items-center gap-1"><RefreshCw size={10} /> New Chat</button>
      </div>
      {showFilters && (
        <div className="px-6 py-2 border-b border-gray-100 bg-white flex gap-3">
          <select value={chatCarrier} onChange={e => setChatCarrier(e.target.value)} className="h-7 text-xs border rounded px-2 bg-white">
            <option value="">All Carriers</option>
            {filters.carriers.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={chatState} onChange={e => setChatState(e.target.value)} className="h-7 text-xs border rounded px-2 bg-white">
            <option value="">All States</option>
            {filters.states.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={chatLob} onChange={e => setChatLob(e.target.value)} className="h-7 text-xs border rounded px-2 bg-white">
            <option value="">All Lines</option>
            {filters.lobs.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
      )}

      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {messages.length === 0 && <EmptyState popularQs={popularQs} onPick={pickQuestion} />}
        {messages.map((msg, i) => <ChatMessage key={msg.id || i} msg={msg} />)}
        {chatLoading && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                Searching guidelines...
              </div>
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div className="px-6 py-3 border-t border-gray-200 bg-white">
        <form onSubmit={e => { e.preventDefault(); handleChat(); }} className="flex gap-2">
          <Input ref={chatInputRef} value={question} onChange={e => setQuestion(e.target.value)}
            placeholder="Ask about carrier appetite, underwriting guidelines..." className="flex-1 h-10 text-sm"
            disabled={chatLoading} data-testid="chat-input" />
          <Button type="submit" disabled={chatLoading || !question.trim()} className="h-10 px-4 bg-[#101C47]" data-testid="chat-send">
            <Send size={14} />
          </Button>
        </form>
      </div>
    </div>
  );
};
